import { useEffect, useState } from "react";
import { GLOBAL_DATE_FORMAT } from "@/constants/date-formats";
import { formatDateWithToken } from "@/utils/date";

export enum MimeType {
    JPEG = "image/jpeg",
    PNG = "image/png",
    PDF = "application/pdf",
}

export type FileViewerFile = {
    id?: string;
    url: string;
    mimeType?: MimeType | string;
    name?: string;
};

type FilePreviewCardProps = {
    files: FileViewerFile[];
    actionLabel?: string;
    previewHeight?: string;
    onAction?: (file: FileViewerFile, index: number) => void;
};

type FileViewerProps = {
    files: FileViewerFile[];
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
    initialIndex?: number;
};

function getFileLabel(file: FileViewerFile, index: number) {
    return file.name || file.id || `Receipt ${index + 1}`;
}

function isImageFile(file: FileViewerFile) {
    return file.mimeType?.startsWith("image/") || /\.(apng|avif|gif|jpe?g|png|webp)$/i.test(file.url);
}

function isPdfFile(file: FileViewerFile) {
    return file.mimeType === MimeType.PDF || /\.pdf(?:$|\?)/i.test(file.url);
}

function FilePreview({ file, label, className = "" }: { file: FileViewerFile; label: string; className?: string }) {
    if (isImageFile(file)) {
        return <img src={file.url} alt={label} className={`h-full w-full object-contain ${className}`} />;
    }

    if (isPdfFile(file)) {
        return <iframe src={file.url} title={label} className={`h-full w-full border-0 ${className}`} />;
    }

    return (
        <div className={`flex h-full w-full items-center justify-center bg-gray-50 text-sm text-gray-500 ${className}`}>
            <a href={file.url} target="_blank" rel="noreferrer" className="text-blue-700 underline">
                Open receipt
            </a>
        </div>
    );
}

export function FilePreviewCard({ files, actionLabel = "View", previewHeight = "h-64", onAction }: FilePreviewCardProps) {
    const [activeIndex, setActiveIndex] = useState(0);
    const activeFile = files[activeIndex];

    useEffect(() => {
        setActiveIndex(0);
    }, [files]);

    if (!activeFile) {
        return null;
    }

    const label = getFileLabel(activeFile, activeIndex);

    return (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className={`${previewHeight} bg-gray-50`}>
                <FilePreview file={activeFile} label={label} />
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-gray-200 px-3 py-2">
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        aria-label="Previous file"
                        disabled={files.length <= 1}
                        onClick={() => setActiveIndex((index) => (index - 1 + files.length) % files.length)}
                        className="rounded px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        Previous
                    </button>
                    <button
                        type="button"
                        aria-label="Next file"
                        disabled={files.length <= 1}
                        onClick={() => setActiveIndex((index) => (index + 1) % files.length)}
                        className="rounded px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        Next
                    </button>
                </div>
                <button
                    type="button"
                    onClick={() => onAction?.(activeFile, activeIndex)}
                    className="rounded bg-blue-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-800"
                >
                    {actionLabel}
                </button>
            </div>
        </div>
    );
}

export function FileViewer({ files, isOpen, onOpenChange, initialIndex = 0 }: FileViewerProps) {
    const [activeIndex, setActiveIndex] = useState(initialIndex);
    const activeFile = files[activeIndex] ?? files[0];
    const label = activeFile ? getFileLabel(activeFile, activeIndex) : "Receipt";

    useEffect(() => {
        setActiveIndex(initialIndex);
    }, [initialIndex, isOpen]);

    if (!isOpen || !activeFile) {
        return null;
    }

    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4" role="dialog" aria-modal="true" aria-label={label}>
            <div className="flex max-h-full w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
                <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
                    <p className="truncate text-sm font-semibold text-gray-900">{label}</p>
                    <button type="button" onClick={() => onOpenChange(false)} className="rounded px-2 py-1 text-sm text-gray-600 hover:bg-gray-100">
                        Close
                    </button>
                </div>
                <div className="h-[75vh] bg-gray-50">
                    <FilePreview file={activeFile} label={label} />
                </div>
                {files.length > 1 && (
                    <div className="flex items-center justify-between gap-3 border-t border-gray-200 px-4 py-3 text-sm text-gray-600">
                        <button
                            type="button"
                            onClick={() => setActiveIndex((index) => (index - 1 + files.length) % files.length)}
                            className="rounded px-3 py-1.5 hover:bg-gray-100"
                        >
                            Previous
                        </button>
                        <span>
                            {activeIndex + 1} of {files.length}
                        </span>
                        <button
                            type="button"
                            onClick={() => setActiveIndex((index) => (index + 1) % files.length)}
                            className="rounded px-3 py-1.5 hover:bg-gray-100"
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export function formatDate(value: string | number | Date | null | undefined, format?: string) {
    if (!value) return "";

    if (format === "MMMM d, yyyy h:mm:ss a") {
        const date = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(date.getTime())) return "";

        const parts = new Intl.DateTimeFormat("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
        }).formatToParts(date);

        const valueFor = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? "";
        return `${valueFor("month")} ${valueFor("day")}, ${valueFor("year")} ${valueFor("hour")}:${valueFor("minute")}:${valueFor("second")} ${valueFor("dayPeriod")}`;
    }

    return formatDateWithToken(value, format ?? GLOBAL_DATE_FORMAT, "");
}
